import { formatPesoAmount } from '../utils/money';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import {
  createComplexPriceRule,
  deleteComplexPriceRule,
  getComplexPriceRules,
  updateComplexPriceRule,
} from '../api';
import useConfirmDialog from '../hooks/useConfirmDialog';
import InlineLoading from './InlineLoading';
import {
  createEmptyComplexPriceRuleDraft,
  formatComplexRangeLabel,
  getComplexRulePreview,
  normalizeComplexPriceRuleDraft,
  validateComplexPriceRuleDraft,
} from '../utils/complexPriceRules';

ChartJS.register(LineElement, PointElement, LinearScale, Tooltip, Legend);

const moneyTick = (value) => `$${formatPesoAmount(Number(value || 0))}`;

const ComplexPriceRulesPanel = ({ canRead = false, canUpdate = false }) => {
  const { confirm } = useConfirmDialog();
  const [rules, setRules] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(createEmptyComplexPriceRuleDraft);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [fieldError, setFieldError] = useState('');

  const selectedRule = useMemo(
    () => rules.find((rule) => String(rule.id) === String(selectedId)) || null,
    [rules, selectedId]
  );

  const selectRule = useCallback((rule) => {
    setSelectedId(rule ? String(rule.id) : 'new');
    setDraft(normalizeComplexPriceRuleDraft(rule));
    setError('');
    setNotice('');
    setFieldError('');
  }, []);

  const loadRules = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError('');
    try {
      const loaded = await getComplexPriceRules();
      setRules(loaded);
      if (loaded.length) selectRule(loaded[0]);
      else selectRule(null);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudieron cargar las reglas complejas.');
    } finally {
      setLoading(false);
    }
  }, [canRead, selectRule]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const patchRange = (index, patch) => {
    setDraft((current) => ({
      ...current,
      ranges: current.ranges.map((range, rangeIndex) => (
        rangeIndex === index ? { ...range, ...patch } : range
      )),
    }));
    setFieldError('');
    setError('');
    setNotice('');
  };

  const addRange = () => {
    setDraft((current) => {
      const ranges = current.ranges.map((range, index) => (
        index === current.ranges.length - 1 ? { ...range, maxCost: '' } : range
      ));
      return {
        ...current,
        ranges: [...ranges, { id: null, maxCost: null, mode: 'markup_percent', value: '0' }],
      };
    });
    setFieldError('');
    setError('');
    setNotice('');
  };

  const removeRange = (index) => {
    setDraft((current) => {
      if (current.ranges.length <= 1) return current;
      const ranges = current.ranges.filter((_, rangeIndex) => rangeIndex !== index);
      return {
        ...current,
        ranges: ranges.map((range, rangeIndex) => (
          rangeIndex === ranges.length - 1 ? { ...range, maxCost: null } : range
        )),
      };
    });
    setFieldError('');
    setError('');
    setNotice('');
  };

  const saveRule = async (event) => {
    event.preventDefault();
    if (!canUpdate || saving) return;
    const validation = validateComplexPriceRuleDraft(draft);
    if (validation.error) {
      setError(validation.error);
      setFieldError(validation.field || '');
      return;
    }
    setSaving(true);
    setError('');
    setFieldError('');
    setNotice('');
    try {
      const saved = selectedRule
        ? await updateComplexPriceRule(selectedRule.id, validation.payload)
        : await createComplexPriceRule(validation.payload);
      if (!saved) throw new Error('La API no devolvió la regla guardada.');
      setRules((current) => {
        const exists = current.some((rule) => Number(rule.id) === Number(saved.id));
        const next = exists
          ? current.map((rule) => Number(rule.id) === Number(saved.id) ? saved : rule)
          : [...current, saved];
        return next.slice().sort((left, right) => String(left.name).localeCompare(String(right.name), 'es'));
      });
      setSelectedId(String(saved.id));
      setDraft(normalizeComplexPriceRuleDraft(saved));
      setNotice('Regla compleja guardada. Los precios actuales no cambian hasta recalcular.');
      window.dispatchEvent(new CustomEvent('complex-price-rules-changed', { detail: { ruleId: saved.id } }));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError?.message || 'No se pudo guardar la regla compleja.');
    } finally {
      setSaving(false);
    }
  };

  const removeSelectedRule = async () => {
    if (!selectedRule || !canUpdate || deleting) return;
    const accepted = await confirm({
      title: '¿Eliminar regla compleja?',
      message: `Se eliminará “${selectedRule.name}”. Solo es posible si no está asignada a ningún formato.`,
      confirmLabel: 'Eliminar regla',
      tone: 'danger',
    });
    if (!accepted) return;
    setDeleting(true);
    setError('');
    setNotice('');
    try {
      await deleteComplexPriceRule(selectedRule.id);
      const next = rules.filter((rule) => Number(rule.id) !== Number(selectedRule.id));
      setRules(next);
      selectRule(next[0] || null);
      setNotice('Regla compleja eliminada.');
      window.dispatchEvent(new CustomEvent('complex-price-rules-changed', { detail: { ruleId: selectedRule.id } }));
    } catch (requestError) {
      const count = Number(requestError?.response?.data?.assignmentCount);
      setError(Number.isFinite(count) && count > 0
        ? `No se puede eliminar: la regla está asignada a ${count} ${count === 1 ? 'formato' : 'formatos'}.`
        : requestError?.response?.data?.message || 'No se pudo eliminar la regla compleja.');
    } finally {
      setDeleting(false);
    }
  };

  const preview = useMemo(() => getComplexRulePreview(draft), [draft]);
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animation: false,
    interaction: { mode: 'nearest', intersect: false },
    scales: {
      x: {
        type: 'linear',
        min: 0,
        suggestedMax: preview.maxCost,
        title: { display: true, text: 'Costo efectivo' },
        ticks: { callback: moneyTick },
      },
      y: {
        type: 'linear',
        beginAtZero: true,
        title: { display: true, text: 'Precio bruto' },
        ticks: { callback: moneyTick },
      },
    },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${moneyTick(context.parsed.y)}`,
        },
      },
    },
  }), [preview.maxCost]);

  if (!canRead) {
    return <div className="rounded-lg border bg-white p-4 text-sm text-gray-600">No tienes permisos para ver reglas complejas de precio.</div>;
  }

  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-semibold text-gray-900">Reglas complejas</h2>
            <p className="mt-1 text-xs text-gray-500">Definiciones compartidas por toda la empresa.</p>
          </div>
          {canUpdate && (
            <button
              type="button"
              onClick={() => selectRule(null)}
              disabled={loading || saving || deleting}
              className="rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              Nueva
            </button>
          )}
        </div>
        {loading ? <div className="mt-4"><InlineLoading label="Cargando reglas" /></div> : (
          <div className="mt-4 space-y-2">
            {rules.map((rule) => {
              const active = String(rule.id) === String(selectedId);
              const count = Number(rule.assignmentCount || 0);
              return (
                <button
                  key={rule.id}
                  type="button"
                  onClick={() => selectRule(rule)}
                  className={`w-full rounded-lg border px-3 py-2 text-left ${active ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <span className="block text-sm font-semibold text-gray-900">{rule.name}</span>
                  <span className="mt-1 block text-xs text-gray-500">
                    {rule.ranges?.length || 0} rangos · {count} {count === 1 ? 'asignación' : 'asignaciones'}
                  </span>
                </button>
              );
            })}
            {!rules.length && (
              <p className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">No hay reglas complejas creadas.</p>
            )}
          </div>
        )}
      </section>

      <form onSubmit={saveRule} className="min-w-0 space-y-4">
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{selectedRule ? 'Editar regla' : 'Nueva regla'}</h2>
              <p className="mt-1 text-sm text-gray-500">Cada costo coincide con un único rango continuo.</p>
            </div>
            {selectedRule && canUpdate && (
              <button
                type="button"
                onClick={removeSelectedRule}
                disabled={saving || deleting}
                className="rounded border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                {deleting ? 'Eliminando…' : 'Eliminar'}
              </button>
            )}
          </div>

          <label className="mt-4 block text-xs font-semibold uppercase text-gray-600">
            Nombre
            <input
              type="text"
              value={draft.name}
              onChange={(event) => {
                setDraft((current) => ({ ...current, name: event.target.value }));
                setFieldError(''); setError(''); setNotice('');
              }}
              disabled={!canUpdate || saving || deleting}
              aria-invalid={fieldError === 'name'}
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3 text-sm font-normal normal-case disabled:bg-gray-100"
              placeholder="Ej. Margen general por costo"
            />
          </label>

          <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-[720px] w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Desde</th>
                  <th className="px-3 py-2 text-left">Hasta (exclusivo)</th>
                  <th className="px-3 py-2 text-left">Cálculo</th>
                  <th className="px-3 py-2 text-left">Valor</th>
                  <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {draft.ranges.map((range, index) => {
                  const minCost = index === 0 ? 0 : draft.ranges[index - 1]?.maxCost;
                  const last = index === draft.ranges.length - 1;
                  return (
                    <tr key={range.id || `draft-${index}`}>
                      <td className="px-3 py-2 font-semibold text-gray-700">${formatPesoAmount(String(minCost || 0))}</td>
                      <td className="px-3 py-2">
                        {last ? <span className="font-semibold text-gray-500">Sin límite</span> : (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={range.maxCost ?? ''}
                            onChange={(event) => patchRange(index, { maxCost: event.target.value })}
                            disabled={!canUpdate || saving || deleting}
                            aria-label={`Límite superior del rango ${index + 1}`}
                            aria-invalid={fieldError === `ranges.${index}.maxCost`}
                            className="h-9 w-32 rounded border border-gray-300 px-2 disabled:bg-gray-100"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={range.mode}
                          onChange={(event) => patchRange(index, { mode: event.target.value })}
                          disabled={!canUpdate || saving || deleting}
                          aria-label={`Cálculo del rango ${index + 1}`}
                          className="h-9 min-w-40 rounded border border-gray-300 px-2 disabled:bg-gray-100"
                        >
                          <option value="markup_percent">Sumar porcentaje</option>
                          <option value="markup_amount">Sumar monto</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <div className="relative w-32">
                          <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-gray-500">
                            {range.mode === 'markup_percent' ? '%' : '$'}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step={range.mode === 'markup_percent' ? 'any' : '1'}
                            value={range.value}
                            onChange={(event) => patchRange(index, { value: event.target.value })}
                            disabled={!canUpdate || saving || deleting}
                            aria-label={`Valor del rango ${index + 1}`}
                            aria-invalid={fieldError === `ranges.${index}.value`}
                            className="h-9 w-full rounded border border-gray-300 pl-6 pr-2 disabled:bg-gray-100"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canUpdate && draft.ranges.length > 1 && (
                          <button type="button" onClick={() => removeRange(index)} disabled={saving || deleting} className="text-xs font-semibold text-red-600 hover:underline">Quitar</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {canUpdate && (
            <button type="button" onClick={addRange} disabled={saving || deleting} className="mt-3 rounded border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60">
              + Agregar rango
            </button>
          )}

          {error && <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {notice && !error && <p role="status" className="mt-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{notice}</p>}
          {canUpdate && (
            <div className="mt-4 flex justify-end">
              <button type="submit" disabled={saving || deleting || loading} className="rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60">
                {saving ? 'Guardando…' : 'Guardar regla'}
              </button>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <h2 className="font-semibold text-gray-900">Vista previa: costo vs. precio</h2>
          <p className="mt-1 text-xs text-gray-500">Muestra el precio bruto sin redondeo. Cada sucursal aplica su redondeo al recalcular.</p>
          <div className="mt-4 h-80" aria-label="Gráfico de costo versus precio">
            {preview.datasets.length ? (
              <Line data={{ datasets: preview.datasets }} options={chartOptions} />
            ) : (
              <div className="flex h-full items-center justify-center rounded border border-dashed border-gray-300 text-sm text-gray-500">Completa los rangos para ver el gráfico.</div>
            )}
          </div>
          <ul className="mt-3 space-y-1 text-xs text-gray-600">
            {(validateComplexPriceRuleDraft(draft).payload?.ranges || []).map((range, index) => (
              <li key={`${range.minCost}-${range.maxCost ?? 'infinity'}`}>{formatComplexRangeLabel(range, index)}</li>
            ))}
          </ul>
        </section>
      </form>
    </div>
  );
};

export default ComplexPriceRulesPanel;
